import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle, TrendingDown, ChevronRight, Activity, Shield,
    Bell, Pill, ClipboardList, ChevronDown, ChevronUp, Clock,
    Radar, Rss,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import CockpitView from "@/components/monitoring/CockpitView";
import TimelineView from "@/components/monitoring/TimelineView";

const BAND_COLORS = {
    critical: { bg: "bg-[#F9ECEC]", border: "border-l-4 border-[#9E2A2B]", text: "text-[#9E2A2B]", chip: "bg-[#9E2A2B] text-white", label: "Crítico" },
    high:     { bg: "bg-[#FBEEE6]", border: "border-l-4 border-[#C85A47]", text: "text-[#C85A47]", chip: "bg-[#C85A47] text-white", label: "Alto" },
    moderate: { bg: "bg-[#FBF4E5]", border: "border-l-4 border-[#D4A373]", text: "text-[#9C6E28]", chip: "bg-[#D4A373] text-white", label: "Moderado" },
    low:      { bg: "bg-[#EDF3E8]", border: "border-l-4 border-[#556B2F]", text: "text-[#556B2F]", chip: "bg-[#556B2F] text-white", label: "Baixo" },
};

const CHIP_TONE = {
    critical: "bg-[#9E2A2B] text-white",
    warning:  "bg-[#D4A373] text-white",
    info:     "bg-[#556B2F] text-white",
};

// Human-friendly relative timestamp — e.g. "hoje", "há 3 dias", "há 2 sem."
function relativeDays(iso) {
    if (!iso) return "—";
    const now = new Date();
    const d = new Date(iso);
    const days = Math.floor((now - d) / 86400000);
    if (days <= 0) return "hoje";
    if (days === 1) return "ontem";
    if (days < 7) return `há ${days}d`;
    if (days < 30) return `há ${Math.floor(days / 7)} sem.`;
    if (days < 365) return `há ${Math.floor(days / 30)} m`;
    return `há ${Math.floor(days / 365)} a`;
}

function StaleBadge({ iso }) {
    if (!iso) return <span className="text-xs text-muted-clinical">sem ação</span>;
    const days = Math.floor((new Date() - new Date(iso)) / 86400000);
    const tone = days >= 30 ? "text-[#9E2A2B]" : days >= 14 ? "text-[#C85A47]" : "text-muted-clinical";
    return <span className={`text-xs ${tone} flex items-center gap-1`}><Clock size={10} />{relativeDays(iso)}</span>;
}

function GroupSection({ band, rows, defaultOpen, meta }) {
    const [open, setOpen] = useState(defaultOpen);
    if (rows.length === 0) return null;
    const c = BAND_COLORS[band];
    return (
        <div className="mb-4">
            <button
                onClick={() => setOpen((v) => !v)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg ${c.bg} border ${c.border.replace('border-l-4 ','')} hover:shadow-sm transition-shadow`}
                data-testid={`risk-group-${band}`}
            >
                <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full ${c.chip}`}>
                        {c.label}
                    </span>
                    <span className={`font-display text-lg font-semibold ${c.text}`}>{rows.length}</span>
                    <span className="text-xs text-muted-clinical">
                        {rows.length === 1 ? "paciente" : "pacientes"} nesta banda
                    </span>
                </div>
                {open ? <ChevronUp size={18} className="text-muted-clinical" /> : <ChevronDown size={18} className="text-muted-clinical" />}
            </button>

            {open && (
                <div className="space-y-2 mt-3">
                    {rows.map((r) => (
                        <Link
                            to={`/monitoring/patient/${r.patient_id}`}
                            key={r.patient_id}
                            data-testid={`risk-row-${r.patient_id}`}
                            className={`card-earthy ${c.bg} ${c.border} block hover:shadow-md transition-shadow`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-3 flex-wrap">
                                        <div className="font-medium text-brand text-lg">{r.patient_name}</div>
                                        {r.post_hospitalization && (
                                            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-accent)] text-white">
                                                Pós-hospitalar
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-clinical mt-1">
                                        {r.insurance || "—"} · Última consulta:{" "}
                                        {r.last_consultation_at ? new Date(r.last_consultation_at).toLocaleDateString("pt-BR") : "—"}
                                    </div>

                                    {/* Reason chips — the "why" of the composite score */}
                                    {r.chips?.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`risk-chips-${r.patient_id}`}>
                                            {r.chips.map((chip, i) => (
                                                <span
                                                    key={i}
                                                    className={`text-[11px] px-2 py-0.5 rounded-full ${CHIP_TONE[chip.tone] || CHIP_TONE.info}`}
                                                >
                                                    {chip.label}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* AGA findings still visible (kept for context) */}
                                    {r.top_findings?.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                                            {r.top_findings.map((f, i) => (
                                                <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-white/60 text-brand border border-[rgba(30,63,51,0.1)]">
                                                    {f}
                                                </span>
                                            ))}
                                            {r.finding_count > 3 && (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-white/60 text-muted-clinical">
                                                    +{r.finding_count - 3}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="text-right shrink-0 min-w-[90px]">
                                    <div className={`font-display text-3xl font-semibold ${c.text}`}>{r.risk_score}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-clinical">score</div>
                                    <div className="mt-1.5" data-testid={`risk-last-action-${r.patient_id}`}>
                                        <StaleBadge iso={r.last_action_at} />
                                    </div>
                                    <ChevronRight size={16} className="ml-auto mt-2 text-muted-clinical" />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function MonitoringPage() {
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("list");

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/monitoring/at-risk");
            // Backward compatibility with old array shape
            const payload = Array.isArray(data) ? { patients: data, totals: {} } : data;
            setRows(payload.patients || []);
            setTotals(payload.totals || {});
            setLoading(false);
        })();
    }, []);

    const grouped = useMemo(() => {
        const g = { critical: [], high: [], moderate: [], low: [] };
        rows.forEach((r) => (g[r.risk_band] || g.low).push(r));
        return g;
    }, [rows]);

    const stats = [
        { icon: AlertTriangle, label: "Críticos",  value: grouped.critical.length, color: "text-[#9E2A2B]" },
        { icon: TrendingDown,  label: "Alto risco", value: grouped.high.length,     color: "text-[#C85A47]" },
        { icon: Bell,          label: "Alertas ativos",       value: totals.critical_alerts ?? 0,             color: "text-[#9E2A2B]", tid: "kpi-active-alerts" },
        { icon: Pill,          label: "Receitas vencendo 7d", value: totals.prescriptions_expiring_7d ?? 0,   color: "text-[#C85A47]", tid: "kpi-rx-expiring" },
    ];

    return (
        <AppShell>
            <div className="mb-6">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">
                    Cuidado proativo
                </div>
                <h1 className="font-display text-3xl lg:text-4xl font-medium text-brand">Painel de seguimento de risco</h1>
                <p className="text-[var(--text-secondary)] mt-1 max-w-3xl">
                    Score composto: AGA (Katz, Lawton, MEEM, GDS-15, Fried, CFS, MAN) + Problem List, calculadoras críticas dos últimos 90 dias e polifarmácia real das receitas emitidas.
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-[rgba(30,63,51,0.1)] mb-6 flex flex-wrap gap-1" data-testid="monitoring-tabs">
                {[
                    { id: "list",     label: "Lista por severidade", Icon: ClipboardList },
                    { id: "cockpit",  label: "Cockpit por síndrome", Icon: Radar },
                    { id: "timeline", label: "Timeline global",      Icon: Rss },
                ].map((t) => {
                    const Icon = t.Icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`monitoring-tab-${t.id}`}
                            className={`px-4 py-2.5 text-sm flex items-center gap-2 rounded-t-lg transition-colors border-b-2 ${active ? "border-brand text-brand font-semibold" : "border-transparent text-muted-clinical hover:text-brand"}`}
                        >
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "list" && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        {stats.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={i} className="card-earthy" data-testid={s.tid}>
                                    <div className="flex items-start justify-between mb-2">
                                        <Icon size={20} className={s.color} />
                                        <span className="text-xs text-muted-clinical uppercase tracking-wider">{s.label}</span>
                                    </div>
                                    <div className={`font-display text-3xl font-semibold ${s.color}`}>{loading ? "…" : s.value}</div>
                                </div>
                            );
                        })}
                    </div>

                    {loading ? (
                        <p className="text-muted-clinical">Carregando…</p>
                    ) : rows.length === 0 ? (
                        <div className="card-earthy text-sm text-muted-clinical">Nenhum paciente com sinais de risco.</div>
                    ) : (
                        <>
                            <GroupSection band="critical" rows={grouped.critical} defaultOpen={true} />
                            <GroupSection band="high"     rows={grouped.high}     defaultOpen={true} />
                            <GroupSection band="moderate" rows={grouped.moderate} defaultOpen={false} />
                            <GroupSection band="low"      rows={grouped.low}      defaultOpen={false} />
                        </>
                    )}
                </>
            )}

            {tab === "cockpit"  && <CockpitView />}
            {tab === "timeline" && <TimelineView />}
        </AppShell>
    );
}
